import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { ReceiptEuro } from "lucide-react";
import t from "@/lang/lang";
import { createContext, ReactNode, useContext, useState } from "react";
import { toast } from "sonner";

/**
 * ConnectInvoiceExportTextContext lets the service specific children of the
 * ConnectInvoiceExportDialog push the text they built into the dialog. The
 * children stay inside the DialogContent on purpose: Radix unmounts it while
 * the dialog is closed, which keeps their queries from running for every work
 * order on the page.
 */
const ConnectInvoiceExportTextContext = createContext<(text: string) => void>(
  () => {},
);

export function useSetConnectInvoiceExportText() {
  return useContext(ConnectInvoiceExportTextContext);
}

export function ConnectInvoiceExportDialog({
  children,
}: {
  children: ReactNode;
}) {
  const [text, setText] = useState("");

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(text);
    toast.success(t("Inhalt in Zwischenablage kopiert"));
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <ReceiptEuro />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>{t("Connect Rechnungsdaten")}</DialogTitle>
          <DialogDescription>
            {t(
              "Hier können Sie die Leistungspositionen für Connect Exportieren. Sie können die Daten im Feld unten ändern. Die Änderungen gehen verloren wenn der Dialog geschlossen wird.",
            )}
          </DialogDescription>
        </DialogHeader>
        <ConnectInvoiceExportTextContext.Provider value={setText}>
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t("Text eingeben...")}
            className="field-sizing-fixed h-[500px] resize-none overflow-auto font-mono"
          />
          {children}
        </ConnectInvoiceExportTextContext.Provider>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Schließen")}</Button>
          </DialogClose>
          <Button onClick={copyToClipboard}>
            {t("In Zwischenablage kopieren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
